import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BookPlus, Trash2, Users, ShoppingBag, DollarSign, BookOpen, Plus, Pencil } from "lucide-react";

const emptyBook = {
  title: "", author: "Baron Vincent de Paul", description: "", price: 9.99,
  original_price: null, pages: 0, language: "Français", category: "Fiction",
  cover_url: "", pdf_url: "", preview_url: "", toc: [], tags: [],
  is_new: true, is_popular: false, is_promo: false, is_audiobook: false,
  audio_url: "", duration_minutes: 0,
};

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [books, setBooks] = useState([]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const reload = async () => {
    const [s, b, u, o, c] = await Promise.all([
      api.get("/admin/stats"), api.get("/books", { params: { limit: 100 } }),
      api.get("/admin/users"), api.get("/admin/orders"), api.get("/admin/coupons"),
    ]);
    setStats(s.data); setBooks(b.data.items); setUsers(u.data); setOrders(o.data); setCoupons(c.data);
  };

  useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

  if (!user) return <div className="max-w-3xl mx-auto px-6 py-24 text-center">Connectez-vous.</div>;
  if (user.role !== "admin") return <div className="max-w-3xl mx-auto px-6 py-24 text-center">Accès refusé.</div>;

  const openNew = () => { setEditing({ ...emptyBook }); setOpen(true); };
  const openEdit = (b) => { setEditing({ ...emptyBook, ...b }); setOpen(true); };

  const saveBook = async () => {
    const payload = { ...editing };
    ["price", "original_price", "pages", "duration_minutes"].forEach((k) => {
      if (payload[k] === "" || payload[k] === null) payload[k] = k === "original_price" ? null : 0;
      else payload[k] = Number(payload[k]);
    });
    if (typeof payload.toc === "string") payload.toc = payload.toc.split("\n").filter(Boolean);
    if (typeof payload.tags === "string") payload.tags = payload.tags.split(",").map(s => s.trim()).filter(Boolean);
    try {
      if (editing.id) await api.put(`/admin/books/${editing.id}`, payload);
      else await api.post("/admin/books", payload);
      toast.success("Enregistré");
      setOpen(false); await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const delBook = async (id) => {
    if (!window.confirm("Supprimer ce livre ?")) return;
    await api.delete(`/admin/books/${id}`); toast.success("Supprimé"); reload();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 fade-in">
      <div className="flex items-center justify-between mb-12">
        <div>
          <div className="label-eyebrow text-accent mb-3">Administration</div>
          <h1 className="font-serif text-5xl tracking-tight">Tableau de bord</h1>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <StatCard icon={DollarSign} label="Revenus" value={`${stats.revenue.toFixed(2).replace(".", ",")} €`} testId="stat-revenue" />
          <StatCard icon={ShoppingBag} label="Commandes" value={stats.total_orders} testId="stat-orders" />
          <StatCard icon={Users} label="Utilisateurs" value={stats.total_users} testId="stat-users" />
          <StatCard icon={BookOpen} label="Livres" value={stats.total_books} testId="stat-books" />
        </div>
      )}

      <Tabs defaultValue="books">
        <TabsList className="rounded-sm">
          <TabsTrigger value="books" data-testid="tab-books">Livres</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Commandes</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="tab-coupons">Coupons</TabsTrigger>
        </TabsList>

        <TabsContent value="books" className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-muted-foreground">{books.length} livre(s)</div>
            <Button data-testid="admin-new-book" onClick={openNew} className="rounded-sm"><Plus className="w-4 h-4 mr-2" /> Nouveau livre</Button>
          </div>
          <div className="space-y-2">
            {books.map((b) => (
              <div key={b.id} className="flex items-center gap-4 p-3 border border-border">
                <img src={b.cover_url} alt="" className="w-12 h-16 object-cover" />
                <div className="flex-1">
                  <div className="font-medium">{b.title}</div>
                  <div className="text-xs text-muted-foreground">{b.category} · {b.price.toFixed(2).replace(".", ",")} € · {b.sales_count || 0} ventes</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`admin-edit-${b.id}`}><Pencil className="w-3 h-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => delBook(b.id)} data-testid={`admin-delete-${b.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-8">
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.id} className="p-4 border border-border grid grid-cols-4 gap-4 text-sm">
                <div className="font-mono text-xs">#{o.id.slice(-8)}</div>
                <div>{new Date(o.created_at).toLocaleDateString("fr-FR")}</div>
                <div>{o.book_ids?.length || 0} livre(s)</div>
                <div className="text-right font-serif">{(o.amount || 0).toFixed(2).replace(".", ",")} €</div>
              </div>
            ))}
            {orders.length === 0 && <div className="text-muted-foreground text-sm">Aucune commande.</div>}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-8">
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} className="p-4 border border-border flex justify-between text-sm">
                <div>
                  <div className="font-medium">{u.name}</div>
                  <div className="text-muted-foreground text-xs">{u.email}</div>
                </div>
                <div className="label-eyebrow text-accent">{u.role}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="coupons" className="mt-8">
          <CouponManager coupons={coupons} onReload={reload} />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifier le livre" : "Nouveau livre"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Titre</Label><Input data-testid="book-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
                <div><Label>Auteur</Label><Input value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} /></div>
              </div>
              <div><Label>Description</Label><Textarea rows={4} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Prix (€)</Label><Input type="number" step="0.01" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></div>
                <div><Label>Prix barré</Label><Input type="number" step="0.01" value={editing.original_price || ""} onChange={(e) => setEditing({ ...editing, original_price: e.target.value })} /></div>
                <div><Label>Pages</Label><Input type="number" value={editing.pages} onChange={(e) => setEditing({ ...editing, pages: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Catégorie</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Fiction","Poésie","Essais","Biographie","Développement personnel"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Langue</Label><Input value={editing.language} onChange={(e) => setEditing({ ...editing, language: e.target.value })} /></div>
              </div>
              <div><Label>Couverture (URL)</Label><Input value={editing.cover_url} onChange={(e) => setEditing({ ...editing, cover_url: e.target.value })} /></div>
              <div><Label>PDF (URL)</Label><Input value={editing.pdf_url} onChange={(e) => setEditing({ ...editing, pdf_url: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_new} onChange={(e) => setEditing({ ...editing, is_new: e.target.checked })} /> Nouveauté</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_popular} onChange={(e) => setEditing({ ...editing, is_popular: e.target.checked })} /> Populaire</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_promo} onChange={(e) => setEditing({ ...editing, is_promo: e.target.checked })} /> Promo</label>
              </div>
              <div><Label>Table des matières (une ligne par entrée)</Label>
                <Textarea rows={4} value={Array.isArray(editing.toc) ? editing.toc.join("\n") : editing.toc}
                  onChange={(e) => setEditing({ ...editing, toc: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Annuler</Button>
            <Button onClick={saveBook} data-testid="book-save" className="rounded-sm">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="p-6 border border-border">
      <Icon className="w-5 h-5 text-accent" />
      <div className="mt-4 label-eyebrow text-muted-foreground">{label}</div>
      <div className="mt-1 font-serif text-3xl">{value}</div>
    </div>
  );
}

function CouponManager({ coupons, onReload }) {
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState(10);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/coupons", { code, percent, active: true });
      toast.success("Coupon créé"); setCode(""); setPercent(10); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => { await api.delete(`/admin/coupons/${id}`); onReload(); };

  return (
    <div>
      <form onSubmit={create} className="flex gap-2 mb-6">
        <Input data-testid="coupon-code" placeholder="CODE" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
        <Input data-testid="coupon-percent" type="number" min="1" max="100" value={percent} onChange={(e) => setPercent(Number(e.target.value))} />
        <Button type="submit" data-testid="coupon-create" className="rounded-sm">Créer</Button>
      </form>
      <div className="space-y-2">
        {coupons.map((c) => (
          <div key={c.id} className="p-3 border border-border flex justify-between items-center">
            <div>
              <div className="font-mono">{c.code}</div>
              <div className="text-xs text-muted-foreground">-{c.percent}%</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => del(c.id)}><Trash2 className="w-3 h-3" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
